import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './style.module.css';
import { NotificationModal } from '../../NotificationModal';
import Typography from '../../Typography/Typography';
import FlatButtons from '../../FlatButton/FlatButton';
import { notification, SellerDashboardDropIcon, StoreHelpIcon, StoreSearchIcon, StoreUserAvatar } from '../../../assets/svgs';
import { useGetStoreByOwnerQuery } from '../../../redux/services/storesApi';
import { setSelectedStore } from '../../../redux/slices/selectedStoreSlice';
import type { RootState } from '../../../redux/store';

interface NotificationItem {
    id: string;
    type: 'order' | 'rating' | 'general';
    title: string;
    message: string;
    time: string;
    isRead: boolean;
    emoji?: string;
}

interface HorizontalNavBarProps {
    currentPage?: string;
    onSearch?: (query: string) => void;
    selectedStore?: string;
    onStoreChange?: (storeId: string) => void;
    notificationCount?: number;
    isVerticalSidebarCollapsed?: boolean;
    notifications?: NotificationItem[];
    onNotificationUpdate?: (notifications: NotificationItem[]) => void;
}

const defaultNotifications: NotificationItem[] = [
    {
        id: '1',
        type: 'order',
        title: 'New order just dropped',
        message: 'Order Info: Customer; Joseph Wheeler, Items; 2, Amount; $200...',
        time: '10:00pm',
        isRead: false,
        emoji: '🎉'
    },
    {
        id: '2',
        type: 'rating',
        title: 'You just received a new rating from a customer',
        message: 'A recent customer just add a review to your store "The store sells really good product...',
        time: '10:00pm',
        isRead: false
    },
    {
        id: '3',
        type: 'order',
        title: 'New order just dropped',
        message: 'Order Info: Customer; Joseph Wheeler, Items; 2, Amount; $200...',
        time: '10:00pm',
        isRead: false,
        emoji: '🎉'
    },
    {
        id: '4',
        type: 'order',
        title: 'New order just dropped',
        message: 'Order Info: Customer; Joseph Wheeler, Items; 2, Amount; $200...',
        time: '10:00pm',
        isRead: false,
        emoji: '🎉'
    },
    {
        id: '5',
        type: 'order',
        title: 'New Order just dropped',
        message: 'Order Info: Customer; Joseph Wheeler, Items; 2, Amount; $200...',
        time: '10:00pm',
        isRead: true,
        emoji: '🎉'
    }
];

export const HorizontalNavBar: React.FC<HorizontalNavBarProps> = ({
    currentPage = 'Dashboard',
    onSearch,
    selectedStore,
    onStoreChange,
    notificationCount: propNotificationCount,
    isVerticalSidebarCollapsed = false,
    notifications: propNotifications,
    onNotificationUpdate
}) => {
    const dispatch = useDispatch();
    const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);
    
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [internalNotifications, setInternalNotifications] = useState<NotificationItem[]>(defaultNotifications);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch stores from API
    const { data: storesData, isLoading, isError } = useGetStoreByOwnerQuery();

    const notifications = propNotifications || internalNotifications;
    const unreadCount = notifications.filter(n => !n.isRead).length;
    const displayNotificationCount = propNotificationCount !== undefined ? propNotificationCount : unreadCount;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleStoreSelect = (storeId: string) => {
        dispatch(setSelectedStore(storeId));
        setIsDropdownOpen(false);
    };

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (onSearch && searchQuery.trim()) {
            onSearch(searchQuery);
            const searchContainer = e.currentTarget.querySelector(`.${styles.searchContainer}`);
            searchContainer?.classList.add(styles.searchSubmitted);
            setTimeout(() => {
                searchContainer?.classList.remove(styles.searchSubmitted);
            }, 300);
        }
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleNotificationClick = () => {
        setIsNotificationModalOpen(true);
    };

    const handleCloseNotificationModal = () => {
        setIsNotificationModalOpen(false);
    };

    const handleMarkAllAsRead = () => {
        const updatedNotifications = notifications.map(n => ({ ...n, isRead: true }));
        
        if (onNotificationUpdate) {
            onNotificationUpdate(updatedNotifications);
        } else {
            setInternalNotifications(updatedNotifications);
        }
    };

    const handleMarkAsRead = (id: string) => {
        const updatedNotifications = notifications.map(n => 
            n.id === id ? { ...n, isRead: true } : n
        );
        
        if (onNotificationUpdate) {
            onNotificationUpdate(updatedNotifications);
        } else {
            setInternalNotifications(updatedNotifications);
        }
    };

    const getSelectedStoreName = () => {
        if (selectedStoreId === 'all') return 'All stores';
        const store = storesData?.find(s => s._id === selectedStoreId);
        return store?.name || 'All stores';
    };

    return (
        <>
            <div className={`${styles.horizontalNavBar} ${isVerticalSidebarCollapsed ? styles.expandedNavbar : ''}`}>
                <div className={styles.navContent}>
                    <div className={styles.leftSection}>
                        <div className={styles.currentPageTitle}>
                            <Typography
                                variant='heading6'
                                fontFamily='satoshi'
                                weight='bold'
                                className={styles.pageTitle}>
                                {currentPage}
                            </Typography>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.storeSelector} ref={dropdownRef}>
                            <div 
                                className={`${styles.storeDropdownContainer} ${isDropdownOpen ? styles.dropdownOpen : ''}`}
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                                <div className={styles.selectedStoreText}>
                                    <Typography
                                        variant='bodyLarge'
                                        fontFamily='satoshi'
                                        weight='bold'
                                        className={styles.storeDropdownText}
                                    >
                                        {isLoading ? 'Loading...' : getSelectedStoreName()}
                                    </Typography>
                                </div>
                                <img
                                    src={SellerDashboardDropIcon}
                                    alt='dropdown icon'
                                    className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.arrowRotated : ''}`}
                                />
                            </div>

                            {isDropdownOpen && (
                                <div className={styles.customDropdownMenu}>
                                    <div 
                                        className={`${styles.dropdownItem} ${selectedStoreId === 'all' ? styles.selected : ''}`}
                                        onClick={() => handleStoreSelect('all')}
                                    >
                                        <span className={styles.storeName}>All stores</span>
                                        {selectedStoreId === 'all' && (
                                            <span className={styles.checkmark}>✓</span>
                                        )}
                                    </div>
                                    
                                    {isLoading && (
                                        <div className={`${styles.dropdownItem} ${styles.loading}`}>
                                            <span className={styles.loadingSpinner}></span>
                                            <span>Loading stores...</span>
                                        </div>
                                    )}
                                    
                                    {isError && (
                                        <div className={`${styles.dropdownItem} ${styles.error}`}>
                                            <span>Error loading stores</span>
                                        </div>
                                    )}
                                    
                                    {storesData && storesData.length > 0 ? (
                                        storesData.map((store) => (
                                            <div 
                                                key={store._id}
                                                className={`${styles.dropdownItem} ${selectedStoreId === store._id ? styles.selected : ''}`}
                                                onClick={() => handleStoreSelect(store._id)}
                                            >
                                                <div className={styles.storeInfo}>
                                                    {store.logo_url && (
                                                        <img src={store.logo_url} alt="" className={styles.storeLogo} />
                                                    )}
                                                    <span className={styles.storeName}>{store.name}</span>
                                                </div>
                                                {selectedStoreId === store._id && (
                                                    <span className={styles.checkmark}>✓</span>
                                                )}
                                            </div>
                                        ))
                                    ) : !isLoading && !isError && (
                                        <div className={`${styles.dropdownItem} ${styles.empty}`}>
                                            <span>No stores found</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
                            <div className={`${styles.searchContainer} ${searchFocused ? styles.searchFocused : ''} ${searchQuery ? styles.hasValue : ''}`}>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchInputChange}
                                    onFocus={() => setSearchFocused(true)}
                                    onBlur={() => setSearchFocused(false)}
                                    placeholder="Search..."
                                    className={styles.searchInput}
                                />
                                <FlatButtons type="submit" className={styles.searchButton}>
                                    <img
                                        src={StoreSearchIcon}
                                        alt='search icon'
                                    />
                                </FlatButtons>
                            </div>
                        </form>

                        <div className={styles.userProfileContainer}>
                            <FlatButtons className={styles.helpButton}>
                                <img
                                    src={StoreHelpIcon}
                                    alt='help icon'
                                />
                            </FlatButtons>

                            <FlatButtons 
                                className={styles.notificationButton}
                                onClick={handleNotificationClick}
                            >
                                <img
                                    src={notification}
                                    alt='notification icon'
                                />
                                {displayNotificationCount > 0 && (
                                    <span className={`${styles.notificationBadge} ${styles.badgePulse}`}>
                                        {displayNotificationCount > 99 ? '99+' : displayNotificationCount}
                                    </span>
                                )}
                            </FlatButtons>
                            
                            <div 
                            onClick={() => { window.location.href = '/profile'; }}
                            className={styles.userAvatar}>
                                <img
                                    src={StoreUserAvatar}
                                    alt="User avatar"
                                    className={styles.avatarImage} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <NotificationModal
                isOpen={isNotificationModalOpen}
                onClose={handleCloseNotificationModal}
                notifications={notifications}
                onMarkAllAsRead={handleMarkAllAsRead}
                onMarkAsRead={handleMarkAsRead}
            />
        </>
    );
};